// src/components/dnl/ViewSwitcher.tsx
/**
 * View Switcher Component - Enhanced Version
 * Phase 5: Alternative Views Feature
 * 
 * Features:
 * - Confirmation dialog for overwriting views
 * - Auto-dismissing tooltip
 * - Keyboard shortcuts (Ctrl+1/2)
 * - Preview mode
 */

import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/rootState';
import {
  captureAsViewA,
  captureAsViewB,
  switchToViewA,
  switchToViewB,
  toggleView,
  dismissTooltip,
  toggleAnimations,
  selectAlternativeViews,
  selectIsViewsConfigured,
  selectCurrentView,
} from '../../store/slices/dnlToolsSlice';
import { Info, X, Eye, Settings } from 'lucide-react';
import ConfirmCaptureDialog from './ConfirmCaptureDialog';
import ViewPreview from './ViewPreview';

export interface ViewSwitcherProps {
  /** Dark mode */
  isDark?: boolean;
  /** Compact mode */
  compact?: boolean;
}

export const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
  isDark = false,
  compact = false,
}) => {
  const dispatch = useDispatch();
  
  // Redux state
  const alternativeViews = useSelector(selectAlternativeViews);
  const isConfigured = useSelector(selectIsViewsConfigured);
  const currentView = useSelector(selectCurrentView);

  // Local state
  const [configMode, setConfigMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    targetView: 'A' | 'B';
  }>({ isOpen: false, targetView: 'A' });
  const [previewDialog, setPreviewDialog] = useState<{
    isOpen: boolean;
    targetView: 'A' | 'B';
  }>({ isOpen: false, targetView: 'A' });

  // Auto-dismiss tooltip after 10 seconds
  useEffect(() => {
    if (isConfigured && alternativeViews.showTooltip) {
      const timer = setTimeout(() => {
        dispatch(dismissTooltip());
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [isConfigured, alternativeViews.showTooltip, dispatch]);

  // Keyboard shortcuts: Alt+1 for View A, Alt+2 for View B
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isConfigured) return;

      // Alt+1 for View A
      if (e.altKey && e.key === '1') {
        e.preventDefault();
        dispatch(switchToViewA());
      }
      // Alt+2 for View B
      else if (e.altKey && e.key === '2') {
        e.preventDefault();
        dispatch(switchToViewB());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, isConfigured]);

  // Handlers
  const handleCaptureRequest = useCallback((targetView: 'A' | 'B') => {
    // Check if view is already configured
    const existingConfig = targetView === 'A' 
      ? alternativeViews.viewA 
      : alternativeViews.viewB;
    
    const hasExistingPoints = existingConfig.points.length > 0;
    const hasExistingLabels = existingConfig.primaryLabel || existingConfig.secondaryLabel;

    if (hasExistingPoints || hasExistingLabels) {
      // Show confirmation dialog
      setConfirmDialog({ isOpen: true, targetView });
    } else {
      // Directly capture
      if (targetView === 'A') {
        dispatch(captureAsViewA());
      } else {
        dispatch(captureAsViewB());
      }
      setConfigMode(false);
    }
  }, [alternativeViews, dispatch]);

  const handleConfirmCapture = useCallback(() => {
    if (confirmDialog.targetView === 'A') {
      dispatch(captureAsViewA());
    } else {
      dispatch(captureAsViewB());
    }
    setConfirmDialog({ isOpen: false, targetView: 'A' });
    setConfigMode(false);
  }, [confirmDialog, dispatch]);

  const handleCancelCapture = useCallback(() => {
    setConfirmDialog({ isOpen: false, targetView: 'A' });
  }, []);

  const handlePreviewRequest = useCallback((targetView: 'A' | 'B') => {
    setPreviewDialog({ isOpen: true, targetView });
  }, []);

  const handleApplyFromPreview = useCallback(() => {
    if (previewDialog.targetView === 'A') {
      dispatch(switchToViewA());
    } else {
      dispatch(switchToViewB());
    }
    setPreviewDialog({ isOpen: false, targetView: 'A' });
  }, [previewDialog, dispatch]);

  const handleClosePreview = useCallback(() => {
    setPreviewDialog({ isOpen: false, targetView: 'A' });
  }, []);

  const handleDismissTooltip = useCallback(() => {
    dispatch(dismissTooltip());
  }, [dispatch]);

  const handleToggleAnimations = useCallback(() => {
    dispatch(toggleAnimations());
  }, [dispatch]);

  // Styles
  const containerClass = `space-y-2 ${compact ? 'text-sm' : ''}`;
  
  const buttonClass = (active: boolean) => `
    flex-1 px-3 py-2 text-sm font-medium rounded transition-all
    ${active
      ? isDark
        ? 'bg-blue-600 text-white shadow-sm'
        : 'bg-blue-500 text-white shadow-sm'
      : isDark
        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }
    ${!isConfigured && 'opacity-50 cursor-not-allowed'}
  `;

  const tooltipClass = `
    p-3 rounded-lg border flex items-start gap-2
    ${isDark
      ? 'bg-blue-900/30 border-blue-700 text-blue-100'
      : 'bg-blue-50 border-blue-200 text-blue-900'
    }
  `;

  const setupButtonClass = `
    px-3 py-1.5 text-xs font-medium rounded transition-colors
    ${isDark
      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }
  `;

  const captureButtonClass = `
    px-3 py-1.5 text-xs font-medium rounded transition-colors
    ${isDark
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-blue-500 text-white hover:bg-blue-600'
    }
  `;

  const previewButtonClass = `
    p-1.5 rounded transition-colors
    ${isDark
      ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
    }
  `;

  return (
    <>
      <div className={containerClass}>
        {/* Header with settings button */}
        <div className="flex items-center justify-between">
          <span className={`text-xs font-medium uppercase tracking-wide ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            View Mode
          </span>
          <div className="flex items-center gap-2">
            {isConfigured && (
              <button
                onClick={handleToggleAnimations}
                className={setupButtonClass}
                title={alternativeViews.animationsEnabled ? 'Disable animations' : 'Enable animations'}
              >
                {alternativeViews.animationsEnabled ? '✓ Animations' : 'Animations'}
              </button>
            )}
            {!configMode && (
              <button
                onClick={() => setConfigMode(true)}
                className={setupButtonClass}
                title="Configure alternative views"
              >
                <Settings size={12} className="inline mr-1" />
                Setup
              </button>
            )}
          </div>
        </div>

        {/* Configuration Mode */}
        {configMode && (
          <div className={`p-3 rounded-lg border space-y-3 ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="text-sm space-y-2">
              <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                Set up your two alternative views:
              </p>
              <ol className={`list-decimal list-inside space-y-1 text-xs ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <li>Configure View A (entity view) with your current setup</li>
                <li>Adjust labels and points for the alternative representation</li>
                <li>Capture as View B (measure view)</li>
                <li>Switch between views as needed</li>
              </ol>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleCaptureRequest('A')}
                className={captureButtonClass}
              >
                Capture as View A
              </button>
              <button
                onClick={() => handleCaptureRequest('B')}
                className={captureButtonClass}
              >
                Capture as View B
              </button>
              <button
                onClick={() => setConfigMode(false)}
                className={setupButtonClass}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* View Switcher (only show when configured) */}
        {!configMode && isConfigured && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button
                onClick={() => dispatch(switchToViewA())}
                className={buttonClass(currentView === 'A')}
                disabled={!isConfigured}
              >
                <div className="flex flex-col items-center">
                  <span>View A</span>
                  <span className="text-xs opacity-75">Entity</span>
                </div>
              </button>
              <button
                onClick={() => dispatch(switchToViewB())}
                className={buttonClass(currentView === 'B')}
                disabled={!isConfigured}
              >
                <div className="flex flex-col items-center">
                  <span>View B</span>
                  <span className="text-xs opacity-75">Measure</span>
                </div>
              </button>
            </div>

            {/* Preview buttons */}
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => handlePreviewRequest('A')}
                className={previewButtonClass}
                title="Preview View A"
              >
                <Eye size={14} className="inline mr-1" />
                <span className="text-xs">Preview A</span>
              </button>
              <button
                onClick={() => handlePreviewRequest('B')}
                className={previewButtonClass}
                title="Preview View B"
              >
                <Eye size={14} className="inline mr-1" />
                <span className="text-xs">Preview B</span>
              </button>
            </div>

            {/* Keyboard shortcut hint */}
            <div className={`text-xs text-center ${
              isDark ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Shortcuts: Alt+1 (View A) | Alt+2 (View B)
            </div>
          </div>
        )}

        {/* Pedagogical Tooltip with auto-dismiss */}
        {!configMode && isConfigured && alternativeViews.showTooltip && (
          <div className={tooltipClass}>
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-xs space-y-1">
              <p className="font-medium">About Alternative Views</p>
              <p className="opacity-90">
                View B keeps similar quantities together—often easier for along-the-line reasoning.
                View A shows each entity's quantities—useful for between-entity comparisons.
              </p>
              <p className="opacity-75 text-[10px] mt-1">
                (Auto-dismisses in 10s)
              </p>
            </div>
            <button
              onClick={handleDismissTooltip}
              className="flex-shrink-0 p-1 rounded hover:bg-black/10"
              title="Dismiss tip"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Status Message when not configured */}
        {!configMode && !isConfigured && (
          <div className={`text-xs text-center py-2 ${
            isDark ? 'text-gray-500' : 'text-gray-400'
          }`}>
            Click "Setup" to configure alternative representations
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmCaptureDialog
        isOpen={confirmDialog.isOpen}
        targetView={confirmDialog.targetView}
        onConfirm={handleConfirmCapture}
        onCancel={handleCancelCapture}
        isDark={isDark}
      />

      {/* Preview Dialog */}
      <ViewPreview
        isOpen={previewDialog.isOpen}
        view={previewDialog.targetView}
        viewConfig={
          previewDialog.targetView === 'A'
            ? alternativeViews.viewA
            : alternativeViews.viewB
        }
        onApply={handleApplyFromPreview}
        onClose={handleClosePreview}
        isDark={isDark}
      />
    </>
  );
};

export default ViewSwitcher;

